/**
 * The editors that write `PUT /me/training-config`. That input is a strict
 * object with every field required, so each save is the WHOLE config — the
 * stored one plus this edit (`trainingConfigInputFrom`), never a partial.
 */
import { trainingConfigOptions } from "@repo/client/data/profile";
import {
  type Goal,
  type TrainingConfigInput,
  type TrainingConfigOutput,
  trainingConfigInput,
} from "@repo/contract/training";
import {
  "profile-editors.apply" as applyText,
  "profile-editors.continue" as continueText,
  "profile-editors.days_title_count" as daysTitleCount,
  "profile-editors.days_title_fixed" as daysTitleFixed,
  "profile-editors.error_field" as errorField,
  "onboarding.experience.question" as experienceQuestion,
  "profile-editors.eyebrow_goal" as eyebrowGoal,
  "profile-editors.eyebrow_routine" as eyebrowRoutine,
  "profile-editors.eyebrow_routine_step" as eyebrowRoutineStep,
  "profile-editors.eyebrow_you" as eyebrowYou,
  "onboarding.goal.question" as goalQuestion,
  "intake.why.goal.long" as goalWhy,
  "onboarding.session_minutes.question" as minutesQuestion,
  "intake.why.session_minutes.long" as minutesWhy,
  "profile-editors.nothing_changed" as nothingChanged,
  "profile-editors.routine_saved" as routineSaved,
  "profile-editors.routine_saved_count" as routineSavedCount,
  "profile-editors.same_change" as sameChange,
  "onboarding.days_fixed.question" as sameQuestion,
  "onboarding.secondary_goal.question" as secondaryQuestion,
  "intake.why.secondary_goal.long" as secondaryWhy,
  "profile-editors.side_helper" as sideHelper,
  "profile-editors.side_title" as sideTitle,
} from "@repo/i18n";
import { useCallback, useState } from "react";
import { useApi } from "../../../data/api";
import { ChoiceForm } from "./choice-form";
import {
  type EditorHost,
  EditorShell,
  useSaveFlow,
  useSectionRead,
} from "./editor-shell";
import {
  type ExperienceDraft,
  ExperienceForm,
  experienceDraftFrom,
  experienceOf,
} from "./experience-form";
import {
  type DominantSide,
  goalOptions,
  type SecondaryChoice,
  secondaryOptions,
  sideOptions,
} from "./options";
import { trainingConfigInputFrom } from "./payloads";
import { RoutineDaysForm, RoutineSameForm, sameOptions } from "./routine-form";
import {
  type RoutineForm,
  resolveRoutine,
  routineFormFrom,
  routineIssue,
  routinePatch,
  routineUnchanged,
  type Same,
  splitKnown,
  switchSame,
} from "./routine-helpers";
import {
  type SessionMinutesDraft,
  SessionMinutesForm,
} from "./session-minutes-form";
import { useEditorForm } from "./use-editor-form";
import { useTrainingConfigMutation } from "./use-editor-mutation";
import { type FieldErrors, FORM_ERROR, NO_ERRORS } from "./validate";
import { strayError } from "./you-editors";

interface EditorProps {
  readonly host: EditorHost;
}

function useConfigEditor<D>(
  host: EditorHost,
  toDraft: (config: TrainingConfigOutput | null) => D,
  toPatch: (
    draft: D,
    config: TrainingConfigOutput | null
  ) => Partial<TrainingConfigInput>,
  options?: {
    readonly detail?: (draft: D) => { message?: string };
    readonly precheck?: (draft: D) => FieldErrors;
  }
) {
  const api = useApi();
  const read = useSectionRead(trainingConfigOptions(api));
  const flow = useSaveFlow(useTrainingConfigMutation(), host);
  const form = useEditorForm({
    onSubmit: (payload: TrainingConfigInput) =>
      flow.save(payload, () => options?.detail?.(form.draft) ?? {}),
    precheck: options?.precheck,
    schema: trainingConfigInput,
    server: read.data,
    toDraft,
    toPayload: (draft: D) =>
      trainingConfigInputFrom(read.data, toPatch(draft, read.data ?? null)),
  });
  return { flow, form, read };
}

export function SideEditor({ host }: EditorProps) {
  const { read, form, flow } = useConfigEditor<DominantSide | null>(
    host,
    (config) => config?.dominantSide ?? null,
    (dominantSide) => ({ dominantSide })
  );
  return (
    <EditorShell
      disabled={
        form.draft === null || form.draft === (read.data?.dominantSide ?? null)
      }
      eyebrow={eyebrowYou()}
      failure={flow.failure ?? strayError(form.errors, ["dominantSide"])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={sideTitle()}
    >
      <ChoiceForm
        helper={sideHelper()}
        label={sideTitle()}
        onChange={form.setDraft}
        options={sideOptions()}
        value={form.draft}
      />
    </EditorShell>
  );
}

export function GoalEditor({ host }: EditorProps) {
  const { read, form, flow } = useConfigEditor<Goal | null>(
    host,
    (config) => config?.primaryGoal ?? null,
    (primaryGoal) => (primaryGoal === null ? {} : { primaryGoal })
  );
  return (
    <EditorShell
      disabled={form.draft === null || form.draft === read.data?.primaryGoal}
      eyebrow={eyebrowGoal()}
      failure={flow.failure ?? strayError(form.errors, ["primaryGoal"])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={goalQuestion()}
    >
      <ChoiceForm
        helper={goalWhy()}
        label={goalQuestion()}
        onChange={form.setDraft}
        options={goalOptions()}
        value={form.draft}
      />
    </EditorShell>
  );
}

export function SecondaryEditor({ host }: EditorProps) {
  const { read, form, flow } = useConfigEditor<SecondaryChoice | null>(
    host,
    (config) =>
      config === null ? null : (config.secondaryGoal ?? ("none" as const)),
    (choice) =>
      choice === null
        ? {}
        : { secondaryGoal: choice === "none" ? null : choice }
  );
  const stored =
    read.data === null ? null : (read.data.secondaryGoal ?? "none");
  return (
    <EditorShell
      disabled={form.draft === null || form.draft === stored}
      eyebrow={eyebrowGoal()}
      failure={flow.failure ?? strayError(form.errors, ["secondaryGoal"])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={secondaryQuestion()}
    >
      <ChoiceForm
        error={
          form.errors.secondaryGoal === undefined ? undefined : errorField()
        }
        helper={secondaryWhy()}
        label={secondaryQuestion()}
        onChange={form.setDraft}
        options={secondaryOptions(read.data?.primaryGoal ?? null)}
        value={form.draft}
      />
    </EditorShell>
  );
}

/** `experience`, `stoppedAgo` and `trainedFor` all open this one body. */
export function ExperienceEditor({ host }: EditorProps) {
  const { read, form, flow } = useConfigEditor<ExperienceDraft>(
    host,
    (config) => experienceDraftFrom(config?.experience ?? null),
    (draft) => {
      const experience = experienceOf(draft);
      return experience === null ? {} : { experience };
    },
    {
      precheck: (draft) =>
        experienceOf(draft) === null ? { experience: "incomplete" } : NO_ERRORS,
    }
  );
  return (
    <EditorShell
      disabled={experienceOf(form.draft) === null}
      eyebrow={eyebrowGoal()}
      failure={flow.failure ?? strayError(form.errors, ["experience"])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={experienceQuestion()}
    >
      <ExperienceForm
        error={form.errors.experience === undefined ? undefined : errorField()}
        onChange={form.setDraft}
        value={form.draft}
      />
    </EditorShell>
  );
}

export function SessionMinutesEditor({ host }: EditorProps) {
  const { read, form, flow } = useConfigEditor<SessionMinutesDraft>(
    host,
    (config) => ({
      hardLimit: config?.sessionMinutesIsHardLimit ?? false,
      minutes: config?.sessionMinutes ?? 60,
    }),
    (draft) => ({
      sessionMinutes: draft.minutes,
      sessionMinutesIsHardLimit: draft.hardLimit,
    })
  );
  const unchanged =
    read.data !== null &&
    form.draft.minutes === read.data.sessionMinutes &&
    form.draft.hardLimit === read.data.sessionMinutesIsHardLimit;
  return (
    <EditorShell
      disabled={unchanged}
      eyebrow={eyebrowRoutine()}
      failure={
        flow.failure ??
        strayError(form.errors, ["sessionMinutes", "sessionMinutesIsHardLimit"])
      }
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={minutesQuestion()}
    >
      <p className="mb-4 text-base text-ink-2 leading-relaxed">
        {minutesWhy()}
      </p>
      <SessionMinutesForm onChange={form.setDraft} value={form.draft} />
    </EditorShell>
  );
}

/**
 * A2 `RoutineSheet` (routine.tsx:103-278): two steps, the onboarding's own
 * questions — "Esses dias são sempre os mesmos?" then "Quais dias?" with the
 * week it would make. `same` opens on step 1, `days` on step 2.
 */
export function RoutineEditor({
  host,
  startAt,
}: EditorProps & { readonly startAt: "days" | "same" }) {
  const [step, setStep] = useState(startAt);
  const { read, form, flow } = useConfigEditor<RoutineForm>(
    host,
    (config) => routineFormFrom(config),
    // The stored config decides whether `preferredSplit` is sent at all: an
    // older server's strict input would refuse the key (`splitKnown`).
    (next, config) => routinePatch(next, config),
    {
      detail: (saved): { message: string } => {
        const { total } = resolveRoutine(saved);
        return {
          message:
            total === read.data?.daysPerWeek
              ? routineSaved()
              : routineSavedCount({ count: total }),
        };
      },
      precheck: (next) =>
        routineIssue(next) === null ? NO_ERRORS : { [FORM_ERROR]: "issue" },
    }
  );
  const { draft, setDraft } = form;
  const setSame = useCallback(
    (same: Same) => setDraft({ ...draft, same }),
    [draft, setDraft]
  );
  const toDays = useCallback(() => {
    setDraft(switchSame(draft, draft.same));
    setStep("days");
  }, [draft, setDraft]);
  const toSame = useCallback(() => setStep("same"), []);

  if (step === "same") {
    return (
      <EditorShell
        eyebrow={eyebrowRoutineStep()}
        host={host}
        loadFailed={read.loadFailed}
        loading={read.loading}
        onSave={toDays}
        saveLabel={continueText()}
        title={sameQuestion()}
      >
        <RoutineSameForm onChange={setSame} value={draft.same} />
      </EditorShell>
    );
  }

  const unchanged = routineUnchanged(draft, read.data);
  const sameLabel =
    sameOptions().find((option) => option.value === draft.same)?.label ?? "";
  return (
    <EditorShell
      disabled={unchanged || routineIssue(draft) !== null}
      eyebrow={
        <button
          className="-my-3 h-12 font-semibold text-accent-ink hover:underline"
          onClick={toSame}
          type="button"
        >
          {sameChange({ label: sameLabel })}
        </button>
      }
      failure={
        flow.failure ??
        strayError(form.errors, ["daysFixed", "daysPerWeek", "fixedWeekdays"])
      }
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      saveLabel={unchanged ? nothingChanged() : applyText()}
      title={draft.same === "none" ? daysTitleCount() : daysTitleFixed()}
    >
      <RoutineDaysForm
        before={read.data?.daysPerWeek ?? null}
        onChange={setDraft}
        splitChoice={splitKnown(read.data ?? null)}
        value={draft}
      />
    </EditorShell>
  );
}
